import React, { useEffect } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import { usePreviewProvider } from 'modules/processes/components/ItemWizard/Providers/preview'
import { useTaskWizard } from 'modules/processes/components/ItemWizard/Tasks/TaskProvider'
import { DataCollectionCategories } from 'modules/processes/domain'

interface ICategory {
  [key: string]: string
}

const FieldSelector = () => {
  const { setTaskConfiguration } = useTaskWizard()
  const { setPreviewValues } = usePreviewProvider()
  const { associatedTaskItem: item, form, setMultiselectOpened } = useFormProvider()

  const currentTaskConfiguration = form.get('task_type_configuration')?.value

  const [selectedCategories, setSelectedCategories] = React.useState<string[]>([])

  const options = [
    {
      label: i18n.t(
        'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.options.general'
      ),
      value: DataCollectionCategories.generalInformation,
    },
    {
      label: i18n.t(
        'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.options.address'
      ),
      value: DataCollectionCategories.address,
    },
    {
      label: i18n.t(
        'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.options.fiscal_residence'
      ),
      value: DataCollectionCategories.fiscal_residence,
    },
    {
      label: i18n.t(
        'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.options.emergency_contact'
      ),
      value: DataCollectionCategories.emergency_contact,
    },
    {
      label: i18n.t(
        'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.options.personal_situation'
      ),
      value: DataCollectionCategories.personalSituation,
    },
  ]

  const extractCategories = (taskTypeConfiguration?: string) => {
    if (!taskTypeConfiguration) return []

    const categories: string[] = (
      JSON.parse(taskTypeConfiguration).configuration_fields?.at(0)?.categories || []
    ).map((category: ICategory) => category.category)
    return categories
  }

  useEffect(() => {
    const currentCategories = currentTaskConfiguration?.configuration_fields
      ? extractCategories(JSON.stringify(currentTaskConfiguration?.configuration_fields))
      : []
    const hasCategorySelected = currentCategories.length > 0

    if (item && item.taskTypeConfiguration && !hasCategorySelected) {
      const categories = extractCategories(item.taskTypeConfiguration)
      setSelectedCategories(categories)
    } else if (hasCategorySelected && currentCategories) {
      setSelectedCategories(currentCategories)
    } else {
      setSelectedCategories([
        DataCollectionCategories.generalInformation,
        DataCollectionCategories.address,
        DataCollectionCategories.fiscal_residence,
        DataCollectionCategories.emergency_contact,
        DataCollectionCategories.personalSituation,
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCategoriesChange = (value: unknown) => {
    setSelectedCategories(value as string[])
  }

  useEffect(() => {
    setMultiselectOpened(false)

    return () => {
      setMultiselectOpened(false)
    }
  }, [])

  // Remove this when the conflict between wizard and multiselect is fixed
  useEffect(() => {
    setTaskConfiguration({
      configuration_fields: [
        {
          resource: 'employee',
          categories: selectedCategories.map((category) => ({ category })),
        },
      ],
    })

    setPreviewValues({ selectedCategories })
  }, [selectedCategories.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  const categoriesOptions = options.map((category) => {
    return (
      <Selector.Option
        key={category.value}
        value={category.value}
        label={category.label}
        searchValue={category.label}
      >
        <Box flexDirection="row" gap="s4" width="full" justifyContent="spaceBetween">
          <Text.Normal ellipsis weight="medium">
            {category.label}
          </Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  return (
    <Selector.Popover
      label={i18n.t(
        'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.label'
      )}
      placeholder={i18n.t('common.select')}
      blankLabel={i18n.t('common.select')}
      value={selectedCategories}
      onChange={onCategoriesChange}
      mandatory
      isMultiSelect
      hasSearch={false}
      footer={
        selectedCategories.length > 0 ? (
          <Box alignItems="center">
            <Button type="secondary" onClick={() => onCategoriesChange([])}>
              {i18n.t(
                'processes.process_settings.wizard.tasks.types.request_general_information.category_selector.clear_selection'
              )}
            </Button>
          </Box>
        ) : undefined
      }
      onToggle={(isOpen) =>
        setTimeout(() => {
          setMultiselectOpened(isOpen)
        }, 25)
      }
    >
      {categoriesOptions}
    </Selector.Popover>
  )
}

export default FieldSelector
